/**
 * The automation's workflow choice: the workflow picker with the mapping
 * clean-up that runs on every switch. The sidebar's workflow step and the
 * settings panel's workflow setting render this one field.
 */
import { notify } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { type FC, useCallback } from 'react';

import type { AutomationConfig } from '../../services/automation';
import type { WorkflowInfo } from '../../services/workflow';
import { WorkflowService } from '../../services/workflow';
import { type MappingCleanupResult } from '../workflow-mapping-diff';
import {
  executeWorkflowSwitch,
  type WorkflowSwitchCommitMode,
  type WorkflowSwitchDeps,
} from '../workflow-switch-handler';
import { WorkflowSelector } from './workflow-selector';

export interface WorkflowFieldProps {
  config: AutomationConfig;
  onUpdate: (updates: Partial<AutomationConfig>) => void;
  readMappings: WorkflowSwitchDeps['readMappings'];
}

export const WorkflowField: FC<WorkflowFieldProps> = ({
  config,
  onUpdate,
  readMappings,
}) => {
  const t = useI18n();
  const workflowService = useService(WorkflowService);

  // sl-8fwi.4: Workflow switch — single entry point for both "pick a
  // different workflow" AND "clear the current workflow". The actual
  // pipeline (snapshot old → load new → diff → commit) lives in the
  // pure helper `executeWorkflowSwitch` so it is unit-testable without
  // mounting the sidebar. This callback only injects the React-bound
  // collaborators (notify, onUpdate, t, workflowService).
  //
  // Pre-fix this was a 150-line useCallback closure with three
  // separate Phase 4b audit findings: #60 (KEEP-vs-CLEAR drift on
  // schema load failure), #61 (WorkflowSelector.onClear bypass), #63
  // (test only exercised the inner pure helper, never the integration).
  const handleWorkflowChange = useCallback(
    (workflow: WorkflowInfo | null) => {
      const oldSchema = workflowService.selectedWorkflowSchema$.value;

      const commit = (
        cleanup: MappingCleanupResult,
        mode: WorkflowSwitchCommitMode,
        committedWorkflow: WorkflowInfo | null
      ) => {
        onUpdate({
          workflowId: committedWorkflow?.id ?? (undefined as unknown as string),
          workflowName:
            committedWorkflow?.name ?? (undefined as unknown as string),
          workflowSteps: [],
          workflowStepsSourceId: null,
          webhookUrlOverride: committedWorkflow?.webhookUrl || null,
          inputMappings: cleanup.cleanInputMappings,
          outputMappings: cleanup.cleanOutputMappings,
        });

        const removed =
          cleanup.removed.inputs.length + cleanup.removed.outputs.length;

        if (mode === 'schema-load-failed') {
          // sl-8fwi.4 (#60): mappings were KEPT, not cleared. Tell the
          // user via a warning toast so they understand the partial
          // state and can recover manually.
          notify.warning({
            title:
              t[
                'com.streamlain.streamlain.workflow.mapping-cleanup.schema-load-failed.title'
              ](),
            message:
              t[
                'com.streamlain.streamlain.workflow.mapping-cleanup.schema-load-failed.message'
              ](),
          });
          return;
        }

        if (removed === 0) {
          return;
        }
        if (removed === 1) {
          notify.success({
            title:
              t[
                'com.streamlain.streamlain.workflow.mapping-cleanup.removed.title'
              ](),
            message:
              t[
                'com.streamlain.streamlain.workflow.mapping-cleanup.removed.one'
              ](),
          });
        } else {
          notify.success({
            title:
              t[
                'com.streamlain.streamlain.workflow.mapping-cleanup.removed.title'
              ](),
            message: t[
              'com.streamlain.streamlain.workflow.mapping-cleanup.removed.many'
            ]({ count: String(removed) }),
          });
        }
      };

      void executeWorkflowSwitch({
        workflow,
        oldSchema,
        readMappings,
        loadSchema: id => workflowService.loadWorkflowSchema(id),
        commit,
      });
    },
    [onUpdate, readMappings, t, workflowService]
  );

  return (
    <WorkflowSelector
      value={config.workflowId}
      onChange={handleWorkflowChange}
      // sl-ai-dropdown-race follow-up: Freshly-created workflows skip
      // the smart-diff pipeline and reset all mappings outright. The
      // old workflow's schema is irrelevant to a blank template and
      // any accidental field-id overlap would leave a half-configured
      // sidebar state.
      onCreate={workflow =>
        onUpdate({
          workflowId: workflow.id,
          workflowName: workflow.name,
          workflowSteps: [],
          workflowStepsSourceId: null,
          webhookUrlOverride: workflow.webhookUrl || null,
          inputMappings: [],
          outputMappings: [],
        })
      }
      // sl-8fwi.4 (#61 fix): Route the clear path through the same
      // handleWorkflowChange entry point. Previously this called
      // onUpdate directly with empty mapping arrays, which silently
      // dropped the cleanup audit trail and showed no toast.
      onClear={() => handleWorkflowChange(null)}
      placeholder={t['com.streamlain.streamlain.process.select-workflow']()}
    />
  );
};
